import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  FileText, 
  Star,
  Calendar,
  Users,
  Award,
  BarChart3,
  AlertCircle,
  Plus,
  Eye,
  ArrowUpRight
} from 'lucide-react';

const VerifierDashboard = () => {
  const [timeframe, setTimeframe] = useState('30d');
  const [metrics, setMetrics] = useState({});
  const [activeProjects, setActiveProjects] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data
  const mockMetrics = {
    totalEarnings: 45750,
    monthlyEarnings: 12300,
    projectsCompleted: 23,
    projectsInProgress: 8,
    averageRating: 4.8,
    responseTime: 24, // hours
    completionRate: 96,
    clientRetention: 89
  };

  const mockActiveProjects = [
    {
      id: 1,
      title: "Amazon Rainforest Conservation",
      type: "Forest Conservation",
      client: "EcoForest Initiative",
      credits: 50000,
      deadline: "2024-02-15",
      progress: 75,
      value: 2500,
      status: "verification-in-progress",
      startDate: "2024-01-10",
      verificationStandard: "Gold Standard"
    },
    {
      id: 2,
      title: "Solar Farm Maharashtra",
      type: "Renewable Energy",
      client: "GreenPower Solutions",
      credits: 80000,
      deadline: "2024-02-20",
      progress: 45,
      value: 1800,
      status: "document-review",
      startDate: "2024-01-15",
      verificationStandard: "VCS"
    },
    {
      id: 3,
      title: "Wind Energy Project Kenya",
      type: "Renewable Energy",
      client: "African Wind Power",
      credits: 60000,
      deadline: "2024-02-25",
      progress: 90,
      value: 3200,
      status: "final-review",
      startDate: "2024-01-05",
      verificationStandard: "CDM"
    }
  ];

  const mockRecentActivity = [
    {
      id: 1,
      type: "verification_completed",
      project: "Reforestation Colombia",
      timestamp: "2024-01-18T10:30:00Z",
      description: "Verification completed and approved"
    },
    {
      id: 2,
      type: "payment_received",
      amount: 2500,
      timestamp: "2024-01-17T15:45:00Z",
      description: "Payment received for Amazon project"
    },
    {
      id: 3,
      type: "new_project_assigned",
      project: "Mangrove Restoration Thailand",
      timestamp: "2024-01-16T09:15:00Z",
      description: "New verification project assigned"
    },
    {
      id: 4,
      type: "client_feedback",
      project: "Wind Energy Project Kenya",
      rating: 5,
      timestamp: "2024-01-15T14:20:00Z",
      description: "Client feedback received"
    }
  ];

  useEffect(() => {
    // Simulate API calls
    setTimeout(() => {
      setMetrics(mockMetrics);
      setActiveProjects(mockActiveProjects);
      setRecentActivity(mockRecentActivity);
      setLoading(false);
    }, 1000);
  }, [timeframe]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'verification-in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'document-review':
        return 'bg-yellow-100 text-yellow-800';
      case 'final-review':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verification-in-progress':
        return 'In Progress';
      case 'document-review':
        return 'Document Review';
      case 'final-review':
        return 'Final Review';
      default:
        return status;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'verification_completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'payment_received':
        return <DollarSign className="h-5 w-5 text-blue-600" />;
      case 'new_project_assigned':
        return <Plus className="h-5 w-5 text-purple-600" />;
      case 'client_feedback':
        return <Star className="h-5 w-5 text-yellow-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Verifier Dashboard</h1>
            <p className="text-gray-600">Welcome back! Here's your verification activity overview.</p>
          </div>
          <div className="flex space-x-3">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <Link
              to="/verifier/marketplace"
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Find Projects
            </Link>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900">${metrics.totalEarnings?.toLocaleString()}</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3 mr-1" />
                  +15% from last period
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Projects Completed</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.projectsCompleted}</p>
                <p className="text-xs text-blue-600 flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3 mr-1" />
                  +3 this month
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Rating</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.averageRating}</p>
                <p className="text-xs text-yellow-600 flex items-center mt-1">
                  <Star className="h-3 w-3 mr-1 fill-current" />
                  Based on {metrics.projectsCompleted} reviews
                </p>
              </div>
              <Award className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Response Time</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.responseTime}h</p>
                <p className="text-xs text-purple-600 flex items-center mt-1">
                  <Clock className="h-3 w-3 mr-1" />
                  Average response
                </p>
              </div>
              <Clock className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Active Projects */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Active Projects</h2>
                <Link 
                  to="/verifier/projects" 
                  className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                >
                  View All
                  <ArrowUpRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {activeProjects.map((project) => (
                  <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="font-medium text-gray-900 mr-3">{project.title}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                            {getStatusText(project.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Client:</span> {project.client}
                          </div>
                          <div>
                            <span className="font-medium">Type:</span> {project.type}
                          </div>
                          <div>
                            <span className="font-medium">Credits:</span> {project.credits.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Value:</span> ${project.value.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Link
                          to={`/verifier/workspace/${project.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100"
                          title="Open Workspace"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Started: {new Date(project.startDate).toLocaleDateString()}</span>
                        <span>Due: {new Date(project.deadline).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {activeProjects.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No active projects</h3>
                  <p className="text-gray-600 mb-4">Browse the marketplace to find new verification opportunities</p>
                  <Link
                    to="/verifier/marketplace"
                    className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Find Projects
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Performance Metrics */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Completion Rate</span>
                <span className="font-medium text-green-600">{metrics.completionRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${metrics.completionRate}%` }}
                ></div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Client Retention</span>
                <span className="font-medium text-blue-600">{metrics.clientRetention}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${metrics.clientRetention}%` }}
                ></div>
              </div>

              <div className="pt-4 border-t">
                <Link
                  to="/verifier/analytics"
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Analytics
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      {activity.description}
                      {activity.amount && (
                        <span className="font-medium text-green-600 ml-1">
                          ${activity.amount.toLocaleString()}
                        </span>
                      )}
                      {activity.rating && (
                        <span className="font-medium text-yellow-600 ml-1">
                          {activity.rating} stars
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/verifier/marketplace"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Find New Projects
              </Link>
              <Link
                to="/verifier/profile"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                <Users className="h-4 w-4 mr-2" />
                Update Profile
              </Link>
              <Link
                to="/verifier/projects"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Manage Projects
              </Link>
            </div>
          </div>

          {/* Alerts & Notifications */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Upcoming Deadlines</h4>
                <p className="text-sm text-yellow-700">
                  You have 2 projects with deadlines in the next 7 days. Review your workspace to ensure timely completion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifierDashboard; 